import type { DrawEvent, DrawEventType, DrawStartEvent } from '@medinbox/angular';
import type {
  AudioDevicePresence,
  AudioPreset,
  Bookmark,
  CameraStatus,
  ChatMessage,
  HeadsetStatus,
  Live,
  LiveState,
  SourceCapture,
  SourceMask,
  SourceMaskData,
} from '@shared/models/live';
import type { Layout, MedinboxConfiguration } from '@shared/models/equipment';
import type { WebSocketMessage } from './web-socket-message';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import type { AudioDeviceFactType } from '@shared/enums/live';

export interface StartStop {
  start: boolean;
}

export interface DrawingToggle extends StartStop {
  whiteboard?: boolean;
}

export interface PrivateTalk extends StartStop {
  /** Left out on purpose to clear the whole exclusive group. */
  headsetId?: number;
  previousState?: HeadsetStatus[];
}

export interface ParticipantRef {
  uuid: string;
}

export interface ApprovalRequest {
  email: string | undefined;
  name: string;
}

export interface ApprovalDecision {
  email: string;
}

export interface LiveStarted {
  LiveCode: string;
  LiveId: number;
}

/** The worker's LiveWebsocketMessage: only code and id are sent when rejoining an existing live. */
export interface EquipmentLiveStarted extends LiveStarted {
  LiveTitle?: string;
  LiveDescription?: string | null;
  LiveStudyType?: string;
  LivePhysician?: string;
}

export interface HeadsetNameChange {
  MedinboxId: string;
  HeadsetId: number;
  Name: string;
  ShortName: string;
}

export interface AudioDeviceFact {
  deviceId: string;
  type: AudioDeviceFactType;
  connectionId: string;
  isMuted?: boolean;
}

/** The front's draw event, tagged with its author and the global "clear all" flag. */
export type DrawWireEvent = (DrawStartEvent | DrawEvent | DrawEventType) & { userId?: string; all?: boolean };

/** The worker's ControlParams. `sourceId` is not read by the worker. */
export interface CameraTarget {
  sourceId: string;
  MedinboxId: string;
  EquipmentId: number;
}

export interface CameraZoom extends CameraTarget {
  Power: number;
}

export interface CameraTiltPan extends CameraTarget {
  Pan: number;
  Tilt: number;
}

export interface CameraSwitch extends CameraTarget {
  On: boolean;
}

export interface HeadsetControl {
  MedinboxId: string;
  HeadsetId: number;
  OtherHeadsetId?: number;
}

export interface FrameFullscreen {
  SourceId: number;
  previousLayoutId: number;
}

export interface FrameSource {
  FrameId: number;
  SourceId: number;
}

export type Recover = { Recover: boolean } | { CanRecover: boolean };

export interface PresetRename {
  Name: string;
  Id: string;
}

export interface LatencyPing {
  latency: number;
  canSendMessage: boolean;
}

export interface EquipmentPing {
  latency: number;
  isBusy: boolean;
}

export interface ChatText {
  text: string;
  email: string | undefined;
}

export interface WaitingParticipant {
  email: string;
  name: string;
}

/** The worker's Response. */
export interface WorkerResponse {
  message: string;
  error?: string | null;
  actionExecuted: boolean;
}

export interface LayoutResponse extends WorkerResponse {
  layout?: Layout | null;
}

/** Every RESULT the worker sends: its Response subclasses carry no discriminant. */
export interface WorkerResult extends LayoutResponse {
  id?: number | null;
  HeadsetStatus?: HeadsetStatus[];
  /** The AIControlResponse's camera; 0 when the action failed. */
  SourceId?: number;
  /** The AIControlResponse's TRACK_BODY or DETECT_SCREEN; the enum default (0) when the action failed. */
  Action?: WebSocketActionEnum;
  Recording?: boolean;
  Recover?: boolean | null;
  CanRecover?: boolean | null;
}

/** The worker's SourceMaskWithUserDto: plain data, not a SourceMask instance. */
export interface MaskSession {
  UserId: string;
  SourceMask: SourceMaskData | null;
}

/** Sent on the live channels (control, draw, chat). */
export interface LiveOutbound {
  [WebSocketActionEnum.APPROVED]: ApprovalDecision;
  [WebSocketActionEnum.DENIED]: ApprovalDecision;
  [WebSocketActionEnum.MUTE]: ParticipantRef;
  [WebSocketActionEnum.CHAT]: ChatText;
  [WebSocketActionEnum.WHITEBOARD]: StartStop;
  [WebSocketActionEnum.DRAW]: DrawWireEvent[];
  [WebSocketActionEnum.DRAWING]: DrawingToggle;
  [WebSocketActionEnum.LASER]: StartStop;
  [WebSocketActionEnum.PAUSE]: undefined;
  [WebSocketActionEnum.RESUME]: undefined;
  [WebSocketActionEnum.RECORD]: StartStop;
  [WebSocketActionEnum.STARTED]: LiveStarted;
  [WebSocketActionEnum.ENDED]: undefined;
  [WebSocketActionEnum.SHARE_SCREEN]: StartStop;
  [WebSocketActionEnum.SEND_VIDEO]: undefined;
  [WebSocketActionEnum.SET_HEADSET_STATUSES]: HeadsetStatus[];
  [WebSocketActionEnum.CHANGE_HEADSET_NAME]: HeadsetNameChange;
  [WebSocketActionEnum.PRIVATE_TALK]: PrivateTalk;
  [WebSocketActionEnum.APPROVAL]: ApprovalRequest;
  [WebSocketActionEnum.RESET_LIVE_TIME]: undefined;
  [WebSocketActionEnum.MUTE_SELF]: ParticipantRef;
  [WebSocketActionEnum.UNMUTE_SELF]: ParticipantRef;
  [WebSocketActionEnum.AUDIO_DEVICE_PRESENCE]: AudioDeviceFact;
}

/** Sent to one participant only: the remote mute carries no payload there. */
export interface UserOutbound {
  [WebSocketActionEnum.MUTE]: undefined;
}

/** Sent to the box. */
export interface EquipmentOutbound {
  [WebSocketActionEnum.ZOOM]: CameraZoom;
  [WebSocketActionEnum.STOPZOOM]: CameraTarget;
  [WebSocketActionEnum.RESETCAMERA]: CameraTarget;
  [WebSocketActionEnum.TILTPAN]: CameraTiltPan;
  [WebSocketActionEnum.TILTPANSTOP]: CameraTarget;
  [WebSocketActionEnum.GET_CONFIGURATION]: undefined;
  /** The layout index as a string, or null to restore the layout saved before fullscreen. */
  [WebSocketActionEnum.LAYOUT]: string | null;
  [WebSocketActionEnum.FRAME_FULLSCREEN]: FrameFullscreen;
  [WebSocketActionEnum.CHANGE_SOURCE]: FrameSource;
  [WebSocketActionEnum.RECORD]: StartStop;
  [WebSocketActionEnum.STARTED]: EquipmentLiveStarted;
  [WebSocketActionEnum.ENDED]: undefined;
  [WebSocketActionEnum.MUTE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.UNMUTE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.ONHOOK_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.OFFHOOK_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.ONLIVE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.OFFLIVE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.ROUTE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.UNROUTE_HEADSET]: HeadsetControl;
  [WebSocketActionEnum.PONG]: undefined;
  [WebSocketActionEnum.CHROME_STARTED]: undefined;
  [WebSocketActionEnum.EXIT]: undefined;
  [WebSocketActionEnum.TRACK_BODY]: CameraSwitch;
  [WebSocketActionEnum.DETECT_SCREEN]: CameraSwitch;
  /** The source id. */
  [WebSocketActionEnum.GET_MASK]: number;
  /** The edited masks, or the bare source id when there are none. */
  [WebSocketActionEnum.REMOVE_IMAGE]: SourceMask | number | null;
  [WebSocketActionEnum.MASK_PING]: number;
  [WebSocketActionEnum.GET_BOOKMARKS]: undefined;
  [WebSocketActionEnum.ADD_BOOKMARK]: Bookmark;
  [WebSocketActionEnum.EDIT_BOOKMARK]: Bookmark;
  [WebSocketActionEnum.DELETE_BOOKMARK]: Bookmark;
  [WebSocketActionEnum.RECOVER]: Recover;
  [WebSocketActionEnum.PRESET_GET_ALL]: undefined;
  /** A preset id. */
  [WebSocketActionEnum.PRESET_APPLY]: string;
  /** A bare name creates or updates by name; an object targets an existing preset. */
  [WebSocketActionEnum.PRESET_SAVE]: string | PresetRename;
  [WebSocketActionEnum.PRESET_DELETE]: string;
}

/** Read from any channel: live, user, draw, chat, latency and box. */
export interface Inbound {
  [WebSocketActionEnum.APPROVED]: ApprovalDecision;
  [WebSocketActionEnum.DENIED]: ApprovalDecision;
  /** No payload on the one-participant channel. */
  [WebSocketActionEnum.MUTE]: ParticipantRef | undefined;
  [WebSocketActionEnum.UNMUTE]: ParticipantRef;
  [WebSocketActionEnum.CHAT_INIT]: ChatMessage[];
  [WebSocketActionEnum.CHAT]: Pick<ChatMessage, 'text' | 'email'>;
  [WebSocketActionEnum.DRAW]: DrawWireEvent[];
  [WebSocketActionEnum.CONFIGURATION_LOADED]: MedinboxConfiguration | null;
  [WebSocketActionEnum.CONFIGURATION_CURRENT]: Layout | null;
  [WebSocketActionEnum.RESULT]: WorkerResult;
  [WebSocketActionEnum.FRAME_FULLSCREEN]: LayoutResponse;
  [WebSocketActionEnum.CHANGE_SOURCE]: LayoutResponse;
  [WebSocketActionEnum.ENDED]: undefined;
  /** The latency socket and the box both send PING, with different payloads. */
  [WebSocketActionEnum.PING]: LatencyPing | EquipmentPing;
  [WebSocketActionEnum.STATE]: LiveState;
  /** Null for a live without any drawing yet. */
  [WebSocketActionEnum.DRAW_INIT]: DrawWireEvent[] | null;
  [WebSocketActionEnum.PARTICIPANTS]: WaitingParticipant[];
  [WebSocketActionEnum.HEADSET_STATE]: HeadsetStatus[];
  [WebSocketActionEnum.LIVE_CONFIGURATION]: Live;
  [WebSocketActionEnum.APPROVAL]: WaitingParticipant;
  [WebSocketActionEnum.CAMERA_STATUS]: CameraStatus;
  /** Elapsed seconds. */
  [WebSocketActionEnum.LIVE_TIME]: number;
  [WebSocketActionEnum.GET_MASK]: MaskSession;
  [WebSocketActionEnum.MASK_IMAGE]: SourceCapture;
  /** Elapsed seconds. */
  [WebSocketActionEnum.RECORD_TIME]: number;
  [WebSocketActionEnum.GET_BOOKMARKS]: Bookmark[];
  [WebSocketActionEnum.MUTE_SELF]: ParticipantRef;
  [WebSocketActionEnum.UNMUTE_SELF]: ParticipantRef;
  [WebSocketActionEnum.CAN_RECOVER]: null;
  [WebSocketActionEnum.PRESET_GET_ALL]: AudioPreset[];
  /** The applied preset id. */
  [WebSocketActionEnum.PRESET_APPLY]: string;
  /** A confirmation text. */
  [WebSocketActionEnum.PRESET_SAVE]: string;
  /** A confirmation text. */
  [WebSocketActionEnum.PRESET_DELETE]: string;
  [WebSocketActionEnum.AUDIO_DEVICES_STATE]: AudioDevicePresence[];
}

/** One `{ action, params }` member per action of the map. */
export type TypedPayload<M> = { [A in keyof M]: { action: A; params: M[A] } }[keyof M];

/** Params may be left out only for actions without a payload. */
export type CommandArgs<P, Target> = undefined extends P ? [params?: P, target?: Target] : [params: P, target?: Target];

export interface InboundWsMessage<A extends keyof Inbound = keyof Inbound> extends Omit<WebSocketMessage, 'message'> {
  message: TypedPayload<Pick<Inbound, A>>;
}

export function isAction<A extends keyof Inbound>(msg: WebSocketMessage, action: A): msg is InboundWsMessage<A> {
  return msg.message.action === action;
}

/** One optional handler per inbound action, each receiving its own narrowed message. */
export type InboundHandlers = { [A in keyof Inbound]?: (message: InboundWsMessage<A>) => void };

export function dispatchInbound(handlers: InboundHandlers, msg: WebSocketMessage): void {
  // Safe: the handler is looked up by this very action.
  (handlers as Partial<Record<WebSocketActionEnum, (message: WebSocketMessage) => void>>)[msg.message.action]?.(msg);
}
